import { Component, computed, inject, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { IConfig } from '@onlyoffice/document-editor-angular';
import { IconComponent } from "src/app/@core/components/ui/icon/icon.component";
import { ButtonComponent } from "src/app/@core/components/ui/button/button.component";
import { TooltipModule } from 'primeng/tooltip';
import { DialogModule } from 'primeng/dialog';
import { AccordionModule } from 'primeng/accordion';
import { MarkdownRenderPipe } from 'src/app/@shared/pipes/markdown-render.pipe';
import { ONLY_OFFICE_EDITOR_TYPE } from 'src/app/@core/models/enums';
import { AuthenticationService } from 'src/app/@core/services/authentication.service';
import { SOCKET_EVENTS, SocketService } from 'src/app/@core/services/socket.service';
import { GEOTECHNICAL_ANALYSIS_SVG, SVG } from 'src/app/common/svgs';
import { GeoTechnicalAnalysisReportDataService } from 'src/app/features/geotechnical-analysis/services/geo-technical-analysis-report-data.service';
import { JWTService } from 'src/app/contract-management/services/jwt.service';
import {
  DASHBOARD_SECTION_TYPE,
  FORMULA_DASHBOARD_STATUS,
  FORMULA_PARAMETER_SOURCE_TYPE,
  IComputedParameterValue,
  IFormulaDashboardSection,
} from 'src/app/features/geotechnical-analysis/services/geotechnical-analysis.service';
import { GenerationLoaderComponent } from 'src/app/@shared/components/generation-loader/generation-loader.component';
import { IAttachment, IUser } from 'src/app/@core/models/model';
import { OnlyOfficeDocEditorComponent } from 'src/app/@shared/components/only-office-doc-editor/only-office-doc-editor.component';
import { environment } from 'src/environments/environment.dev';
import { ToastService } from 'src/app/@core/services/toast.service';

@Component({
  selector: 'app-formula-tab',
  standalone: true,
  imports: [CommonModule, FormsModule, TooltipModule, DialogModule, AccordionModule, IconComponent, MarkdownRenderPipe, ReactiveFormsModule, OnlyOfficeDocEditorComponent, ButtonComponent, GenerationLoaderComponent],
  templateUrl: './formula-tab.component.html',
  styleUrl: './formula-tab.component.scss'
})
export class FormulaTabComponent implements OnDestroy {
  GEOTECHNICAL_ANALYSIS_SVG = GEOTECHNICAL_ANALYSIS_SVG;
  SVG = SVG;
  DASHBOARD_SECTION_TYPE = DASHBOARD_SECTION_TYPE;
  FORMULA_DASHBOARD_STATUS = FORMULA_DASHBOARD_STATUS;
  FORMULA_PARAMETER_SOURCE_TYPE = FORMULA_PARAMETER_SOURCE_TYPE;

  private geoReportDataService = inject(GeoTechnicalAnalysisReportDataService);
  private authService = inject(AuthenticationService);
  private socketService = inject(SocketService);
  private jwtService = new JWTService();
  private socketSubscription?: Subscription;

  currentUser: IUser | null = null;

  dashboard = this.geoReportDataService.formulaDashboard;
  loading = this.geoReportDataService.formulaDashboardLoading;
  errorMessage = this.geoReportDataService.formulaDashboardError;

  analysisId = this.geoReportDataService.analysisReportId();


  sections = computed(() => this.dashboard()?.sections ?? []);
  status = computed(() => this.dashboard()?.status ?? FORMULA_DASHBOARD_STATUS.NOT_STARTED);
  isProcessing = computed(() => this.loading() || this.status() === FORMULA_DASHBOARD_STATUS.PROCESSING);
  isFailed = computed(() => !this.loading() && this.status() === FORMULA_DASHBOARD_STATUS.FAILED);
  isEmpty = computed(() =>
    !this.loading() &&
    !this.isFailed() &&
    (this.status() === FORMULA_DASHBOARD_STATUS.NOT_STARTED || this.sections().length === 0)
  );

  generationAnimationData: unknown;
  private _analysisExcelDialogVisible = false;
  get analysisExcelDialogVisible(): boolean { return this._analysisExcelDialogVisible; }
  set analysisExcelDialogVisible(value: boolean) {
    this._analysisExcelDialogVisible = value;
    if (value) {
      this.geoReportDataService.refreshAnalysisExcelUrl();
    }
  }
  analysisExcelPreviewFile = computed<IAttachment | null>(() => {
    const analysisReport = this.geoReportDataService.analysisReport();
    const analysisId = this.geoReportDataService.analysisReportId();
    const analysisFileUrl = analysisReport?.analysisExcelUrl?.trim();

    if (!analysisId || !analysisReport || !analysisFileUrl) {
      return null;
    }

    const analysisName = analysisReport.displayName
      || analysisReport.name
      || 'Analysis Excel';

    return {
      _id: analysisId,
      filename: `${analysisName}.xlsx`,
      url: analysisFileUrl,
      fullUrl: analysisFileUrl,
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      isPublic: false,
    };
  });
  private urlVersionHash(url: string): string {
    // Extract versionId from S3 versioned URL if present, otherwise use a numeric hash of the URL
    const versionMatch = url.match(/[?&]versionId=([^&]+)/);
    if (versionMatch) return versionMatch[1].slice(0, 16);
    let hash = 0;
    for (let i = 0; i < url.length; i++) {
      hash = (Math.imul(31, hash) + url.charCodeAt(i)) | 0;
    }
    return Math.abs(hash).toString(36);
  }

  analysisExcelEditorConfig = computed<IConfig | null>(() => {
    const previewFile = this.analysisExcelPreviewFile();
    const analysisId = this.geoReportDataService.analysisReportId();
    const canEdit = this.geoReportDataService.permissions()?.canEdit ?? false;

    if (!previewFile || !analysisId) {
      return null;
    }

    const docUrl = previewFile.fullUrl ?? previewFile.url;
    const versionHash = this.urlVersionHash(docUrl);

    const tokenConfig: Partial<IConfig> = {
      document: {
        fileType: 'xlsx',
        key: `${analysisId}-analysis-excel-${versionHash}`,
        title: previewFile.filename,
        url: docUrl,
        permissions: {
          chat: canEdit,
          comment: canEdit,
          download: true,
          edit: canEdit,
          fillForms: canEdit,
          modifyContentControl: canEdit,
          modifyFilter: canEdit,
          print: true,
          protect: canEdit,
          review: canEdit,
          userInfoGroups: [],
        },
      },
      documentType: ONLY_OFFICE_EDITOR_TYPE.EXCEL,
      editorConfig: {
        user: {
          group: analysisId,
          id: this.currentUser?.email ?? '',
          image: this.currentUser?.profilePicture?.url
            ? `${environment.s3Url}/${this.currentUser.profilePicture.url}?timestamp=${new Date().getTime()}`
            : '',
          name: `${this.currentUser?.firstName ?? ''} ${this.currentUser?.lastName ?? ''}`.trim(),
        },
        embedded: {
          toolbarDocked: 'top',
        },
        customization: {
          toolbarHideFileName: true,
          hideRightMenu: true,
          compactToolbar: true,
          compactHeader: true,
          comments: canEdit,
          forcesave: true,
          autosave: false,
          uiTheme: 'theme-gray',
          logo: {
            image: `${environment.s3Url}/inndoc-ai/assets/images/3.png`,
          },
          zoom: -2,
        },
        callbackUrl: `${environment.apiUrl}/public/save-geotechnical-analysis-excel?analysisId=${analysisId}&userId=${this.currentUser?._id ?? ''}`,
      },
      height: '100%',
      width: '100%',
    };

    return {
      ...tokenConfig,
      token: this.jwtService.signToken(tokenConfig, environment.jwtSecret),
    } as IConfig;
  });

  aiSourceDialogVisible = false;
  aiSourceDialogParam: IComputedParameterValue | null = null;
  aiSourceIndex = 0;

  constructor(private toastService: ToastService) {
    this.authService.currentUser.subscribe((user) => {
      this.currentUser = user ?? null;
    });
  }

  ngOnInit(): void {
    this.loadGenerationAnimation();
    this.initSocketListeners();
  }

  ngOnDestroy(): void {
    this.socketSubscription?.unsubscribe();
  }

  private initSocketListeners(): void {
    this.socketSubscription = this.socketService
      .on<{ geotechnicalAnalysisId: string; success: boolean; loading?: boolean; error?: string }>(SOCKET_EVENTS.GEOTECHNICAL_FORMULA_DASHBOARD)
      .subscribe(([data]) => {
        if (data?.success) {
          this.geoReportDataService.loadFormulas();
          if (!data?.loading) {
            this.toastService.success("Formula dashboard generation completed");
          }
        } else {
          this.geoReportDataService.setFormulaDashboardFailed(data?.error);
          this.toastService.error("Formula dashboard generation failed");
        }
      });
  }

  getSectionId(section: IFormulaDashboardSection): string {
    return String(section._id);
  }

  getParameterValue(nameKey: string): IComputedParameterValue | undefined {
    return this.dashboard()?.parameterValues?.find((item) => item.nameKey === nameKey);
  }

  getParameterDisplayValue(nameKey: string): string {
    const parameterValue = this.getParameterValue(nameKey);
    const value = parameterValue?.value?.trim();
    const unit = parameterValue?.unit?.trim();
    if (value && unit) return `${value} ${unit}`;
    if (value) return value;
    if (value && unit) return unit;
    return '';
  }

  openSourceDialog(nameKey: string): void {
    const param = this.getParameterValue(nameKey);
    if (!param?.sourceType) return;
    if (param.sourceType === FORMULA_PARAMETER_SOURCE_TYPE.AI_EXTRACTED) {
      this.aiSourceDialogParam = param;
      this.aiSourceIndex = 0;
      this.aiSourceDialogVisible = true;
    } else if (param.sourceType === FORMULA_PARAMETER_SOURCE_TYPE.ANALYSIS_EXCEL) {
      this.analysisExcelDialogVisible = true;
    }
  }

  navigateSource(direction: number): void {
    const total = this.aiSourceDialogParam?.source?.length ?? 0;
    this.aiSourceIndex = Math.max(0, Math.min(total - 1, this.aiSourceIndex + direction));
  }

  loadGenerationAnimation(): void {
    fetch('/animations/template_generation.json')
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        this.generationAnimationData = data;
      })
      .catch((error) => {
        console.error('Error loading template generation animation:', error);
      });
  }
}
